import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import {
  CreateTreatmentRecordRequestChannelSchema,
  TreatmentRecordRequestChannelType,
  VoiceAiCallArgs,
} from '@/services/api/generated/treatmentRecordRequestsService';
import {
  useGetMatterDetailsQuery,
  useGetMatterQuery,
  usePatchMatterClientMutation,
} from '@/services/api/mattersService';
import { treatmentRecordRequestService } from '@/services/api/treatmentRecordRequestService';

// Shared type for the form values
export type VoiceAICallFormValues = {
  patient_information: {
    client_name: string;
    client_date_of_birth: string;
  };
  request_details: {
    request_method: TreatmentRecordRequestChannelType;
    request_sent_at: string;
    request_method_target: string;
    contact_number: string;
  };
};

// Forgive me for the slop I have commited - mm

// Populate form values from APIs (request, channel, matter, client)
export const useVoiceAICallFormData = ({
  matterId,
  providerId,
  requestId,
}: {
  matterId: string;
  providerId: string;
  requestId: string;
}): {
  values: VoiceAICallFormValues;
  setValues: React.Dispatch<React.SetStateAction<VoiceAICallFormValues>>;
  handleChange: (
    section: keyof VoiceAICallFormValues,
    field: string,
    value: string,
  ) => void;
  isLoading: boolean;
} => {
  const { data: request } =
    treatmentRecordRequestService.useMattersProvidersRecordRequestsRetrieveQuery(
      {
        matterId,
        providerId,
        requestId,
      },
    );
  const { data: matterDetails } = useGetMatterDetailsQuery(matterId);

  const [values, setValues] = useState<VoiceAICallFormValues>({
    patient_information: {
      client_name: '',
      client_date_of_birth: '',
    },
    request_details: {
      request_method: 'portal',
      request_sent_at: new Date().toISOString(),
      request_method_target: '',
      contact_number: '',
    },
  });

  useEffect(() => {
    if (!request) return;

    setValues((prev) => {
      const next = { ...prev };
      const stored = request.metadata?.ad_hoc_voice_ai_args as
        | VoiceAiCallArgs
        | undefined;

      if (stored?.patient_information) {
        next.patient_information = {
          ...next.patient_information,
          ...stored.patient_information,
        };
      }
      if (stored?.request_details) {
        next.request_details = {
          ...next.request_details,
          ...stored.request_details,
        };
      }

      // Prefer existing request metadata for submitted date if present
      if (request.metadata?.request_submitted_at) {
        next.request_details.request_sent_at =
          request.metadata.request_submitted_at;
      }

      if (request.channel?.type && !stored?.request_details?.request_method) {
        next.request_details.request_method = request.channel.type;
      }

      // Backfill target/contact from channel if empty
      if (request.channel) {
        if (!next.request_details.request_method_target) {
          if (request.channel.type === 'fax' && request.channel.fax) {
            next.request_details.request_method_target = request.channel.fax;
          } else if (
            request.channel.type === 'email' &&
            request.channel.email
          ) {
            next.request_details.request_method_target = request.channel.email;
          } else if (
            request.channel.type === 'mail' &&
            request.channel.address
          ) {
            next.request_details.request_method_target =
              request.channel.address;
          }
        }
        if (
          !next.request_details.contact_number &&
          request.channel.phone_number
        ) {
          next.request_details.contact_number = request.channel.phone_number;
        }
      }

      // Backfill patient info from matter/client: prefer client name, otherwise matter name
      if (matterDetails) {
        type ClientLite = {
          name?: string;
          date_of_birth?: string;
          is_primary?: boolean;
        };
        const clients = matterDetails.clients as ClientLite[] | undefined;
        const primaryClient =
          clients?.find((c) => c.is_primary) || clients?.[0];

        if (!next.patient_information.client_name) {
          next.patient_information.client_name =
            primaryClient?.name || matterDetails.name || '';
        }
        if (
          !next.patient_information.client_date_of_birth &&
          primaryClient?.date_of_birth
        ) {
          next.patient_information.client_date_of_birth =
            primaryClient.date_of_birth;
        }
      }

      return next;
    });
  }, [request, matterDetails]);

  const handleChange = (
    section: keyof VoiceAICallFormValues,
    field: string,
    value: string,
  ): void => {
    setValues((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));
  };

  const isLoading = !request;

  return { values, setValues, handleChange, isLoading };
};

// Save/mutation hook: update client, create/update channel, then refetch request
export const useSaveVoiceAICallFirm = ({
  matterId,
  providerId,
  requestId,
}: {
  matterId: string;
  providerId: string;
  requestId: string;
}) => {
  const { data: matter } = useGetMatterQuery(matterId);

  const { data: request, refetch: refetchRequest } =
    treatmentRecordRequestService.useMattersProvidersRecordRequestsRetrieveQuery(
      {
        matterId,
        providerId,
        requestId,
      },
    );

  const [patchMatterClient] = usePatchMatterClientMutation();
  const [createChannel] =
    treatmentRecordRequestService.useMattersProvidersRecordRequestsChannelsCreateMutation();
  const [updateChannel] =
    treatmentRecordRequestService.useMattersProvidersRecordRequestsChannelsUpdateMutation();
  const [updateRequest] =
    treatmentRecordRequestService.useMattersProvidersRecordRequestsUpdateMutation();

  const primaryClientId = useMemo(() => {
    if (!matter?.clients?.length) return undefined;
    const clients = matter.clients as { id: string; is_primary?: boolean }[];
    const primary = clients.find((c) => c.is_primary);
    return (primary || clients[0])?.id as string | undefined;
  }, [matter]);

  const buildChannelSchemaFromRequestDetails = (
    details: VoiceAICallFormValues['request_details'],
  ): CreateTreatmentRecordRequestChannelSchema => {
    const schema: CreateTreatmentRecordRequestChannelSchema = {
      type: details.request_method,
    };
    if (details.request_method === 'fax') {
      schema.fax = details.request_method_target || null;
      schema.phone_number = details.contact_number || null;
    } else if (details.request_method === 'email') {
      schema.email = details.request_method_target || null;
      schema.phone_number = details.contact_number || null;
    } else if (details.request_method === 'mail') {
      schema.address = details.request_method_target || null;
      schema.phone_number = details.contact_number || null;
    } else if (details.request_method === 'human') {
      schema.phone_number = details.contact_number || null;
    } else if (details.request_method === 'portal') {
      schema.phone_number = details.contact_number || null;
    }
    return schema;
  };

  const saveVoiceAicallFirm = useCallback(
    async (values: VoiceAICallFormValues): Promise<void> => {
      try {
        if (primaryClientId) {
          await patchMatterClient({
            matter_id: matterId,
            matter_client_id: primaryClientId,
            patch: {
              name: values.patient_information.client_name,
              date_of_birth: values.patient_information.client_date_of_birth,
            },
          }).unwrap();
        }

        const channelSchema = buildChannelSchemaFromRequestDetails(
          values.request_details,
        );

        if (!request?.channel) {
          await createChannel({
            matterId,
            providerId,
            requestId,
            createTreatmentRecordRequestChannelSchema: channelSchema,
          }).unwrap();
        } else {
          await updateChannel({
            matterId,
            providerId,
            requestId,
            channelId: request.channel.id,
            updateTreatmentRecordRequestChannelSchema: channelSchema,
          }).unwrap();
        }

        // 3) Persist submitted_at on request metadata
        await updateRequest({
          matterId,
          providerId,
          requestId,
          updateTreatmentRecordRequestSchema: {
            request_id: requestId,
            metadata: {
              ...(request?.metadata || {}),
              request_submitted_at: values.request_details.request_sent_at,
            },
          },
        }).unwrap();

        await refetchRequest();
        toast.success('Voice AI call details saved');
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to save Voice AI call details', error);
        toast.error('Failed to save Voice AI call details');
        throw error;
      }
    },
    [
      matterId,
      providerId,
      requestId,
      primaryClientId,
      request?.channel,
      request?.metadata,
      patchMatterClient,
      createChannel,
      updateChannel,
      refetchRequest,
      updateRequest,
    ],
  );

  return { saveVoiceAicallFirm };
};
